import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ArrowLeft, Save } from "lucide-react";

const MEDIA_TYPES = [
    { value: "tv", label: "Televisão" },
    { value: "radio", label: "Rádio" },
    { value: "print", label: "Imprensa" },
    { value: "online", label: "Online" },
];

const EMPTY = {
    title: "",
    media_outlet: "",
    media_type: "online",
    date_iso: new Date().toISOString().slice(0, 10),
    excerpt: "",
    url: "",
    image_url: "",
    featured: false,
};

const PressForm = () => {
    const { authAxios } = useAuth();
    const navigate = useNavigate();
    const { pressId } = useParams();
    const isEdit = Boolean(pressId);

    const [form, setForm] = useState(EMPTY);
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(isEdit);

    useEffect(() => {
        if (!isEdit) return;
        authAxios
            .get("/admin/press")
            .then((r) => {
                const item = r.data.find((i) => i.id === pressId);
                if (!item) { toast.error("Item não encontrado"); navigate("/admin/press"); return; }
                setForm({
                    title: item.title,
                    media_outlet: item.media_outlet,
                    media_type: item.media_type,
                    date_iso: item.date_iso,
                    excerpt: item.excerpt || "",
                    url: item.url || "",
                    image_url: item.image_url || "",
                    featured: item.featured || false,
                });
            })
            .catch(() => toast.error("Erro ao carregar item"))
            .finally(() => setLoading(false));
    }, [pressId]); // eslint-disable-line

    const set = (field) => (e) => setForm((f) => ({
        ...f,
        [field]: e.target.type === "checkbox" ? e.target.checked : e.target.value,
    }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.title.trim() || !form.media_outlet.trim()) {
            toast.error("Preencha o título e o nome do meio de comunicação");
            return;
        }
        setSaving(true);
        try {
            if (isEdit) {
                await authAxios.put(`/admin/press/${pressId}`, form);
                toast.success("Item atualizado");
            } else {
                await authAxios.post("/admin/press", form);
                toast.success("Item criado");
            }
            navigate("/admin/press");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Erro ao guardar");
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div className="text-center py-16 text-[color:var(--acd-muted)]">A carregar...</div>;

    return (
        <div data-testid="press-form">
            <div className="flex items-center gap-4 mb-8">
                <button
                    onClick={() => navigate("/admin/press")}
                    className="w-9 h-9 flex items-center justify-center rounded-lg border border-gray-200 hover:bg-gray-50 transition"
                    data-testid="press-form-back"
                >
                    <ArrowLeft size={16} />
                </button>
                <div>
                    <h1 className="text-2xl font-bold text-[var(--acd-navy-900)]">
                        {isEdit ? "Editar item" : "Novo item de imprensa"}
                    </h1>
                    <p className="text-sm text-[color:var(--acd-muted)]">
                        {isEdit ? "Atualize os detalhes da publicação" : "Adicione uma nova aparição nos media"}
                    </p>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-gray-100 p-8 space-y-6 max-w-2xl">
                {/* Title */}
                <div>
                    <label className="block text-sm font-semibold text-[var(--acd-navy-900)] mb-2">
                        Título da publicação *
                    </label>
                    <input
                        className="acd-input w-full"
                        placeholder="Ex: Novo regime do arrendamento comentado por especialistas"
                        value={form.title}
                        onChange={set("title")}
                        required
                        data-testid="press-form-title"
                    />
                </div>

                {/* Outlet + Type */}
                <div className="grid md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-semibold text-[var(--acd-navy-900)] mb-2">
                            Meio de comunicação *
                        </label>
                        <input
                            className="acd-input w-full"
                            placeholder="Ex: TSF Rádio, Expresso, RTP..."
                            value={form.media_outlet}
                            onChange={set("media_outlet")}
                            required
                            data-testid="press-form-outlet"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-semibold text-[var(--acd-navy-900)] mb-2">
                            Tipo de média *
                        </label>
                        <select
                            className="acd-input w-full"
                            value={form.media_type}
                            onChange={set("media_type")}
                            data-testid="press-form-type"
                        >
                            {MEDIA_TYPES.map((t) => (
                                <option key={t.value} value={t.value}>{t.label}</option>
                            ))}
                        </select>
                    </div>
                </div>

                {/* Date */}
                <div>
                    <label className="block text-sm font-semibold text-[var(--acd-navy-900)] mb-2">
                        Data de publicação *
                    </label>
                    <input
                        type="date"
                        className="acd-input w-full md:w-48"
                        value={form.date_iso}
                        onChange={set("date_iso")}
                        required
                        data-testid="press-form-date"
                    />
                </div>

                {/* Excerpt */}
                <div>
                    <label className="block text-sm font-semibold text-[var(--acd-navy-900)] mb-2">
                        Descrição / Excerto
                    </label>
                    <textarea
                        className="acd-input w-full"
                        rows={3}
                        placeholder="Breve descrição da publicação ou uma citação relevante..."
                        value={form.excerpt}
                        onChange={set("excerpt")}
                        data-testid="press-form-excerpt"
                    />
                </div>

                {/* URL */}
                <div>
                    <label className="block text-sm font-semibold text-[var(--acd-navy-900)] mb-2">
                        Link para a publicação original (opcional)
                    </label>
                    <input
                        type="url"
                        className="acd-input w-full"
                        placeholder="https://..."
                        value={form.url}
                        onChange={set("url")}
                        data-testid="press-form-url"
                    />
                </div>

                {/* Image URL */}
                <div>
                    <label className="block text-sm font-semibold text-[var(--acd-navy-900)] mb-2">
                        URL da imagem de destaque (opcional)
                    </label>
                    <input
                        type="url"
                        className="acd-input w-full"
                        placeholder="https://..."
                        value={form.image_url}
                        onChange={set("image_url")}
                        data-testid="press-form-image-url"
                    />
                    {form.image_url && (
                        <div className="mt-2 rounded-lg overflow-hidden h-24 border border-gray-200">
                            <img src={form.image_url} alt="Preview" className="h-full w-full object-cover" />
                        </div>
                    )}
                </div>

                {/* Featured toggle */}
                <div className="flex items-center gap-3 p-4 bg-[var(--acd-bg-soft)] rounded-lg">
                    <input
                        type="checkbox"
                        id="featured-toggle"
                        className="w-4 h-4 accent-[var(--acd-navy-900)]"
                        checked={form.featured}
                        onChange={set("featured")}
                        data-testid="press-form-featured"
                    />
                    <label htmlFor="featured-toggle" className="text-sm font-semibold text-[var(--acd-navy-900)] cursor-pointer">
                        Destaque principal
                        <span className="block text-xs text-[color:var(--acd-muted)] font-normal mt-0.5">
                            Aparece em destaque no topo da página Press (apenas um item de cada vez)
                        </span>
                    </label>
                </div>

                {/* Actions */}
                <div className="flex gap-3 pt-2">
                    <button
                        type="submit"
                        className="acd-btn-gradient"
                        disabled={saving}
                        data-testid="press-form-submit"
                    >
                        <Save size={16} />
                        {saving ? "A guardar..." : isEdit ? "Atualizar" : "Criar item"}
                    </button>
                    <button
                        type="button"
                        onClick={() => navigate("/admin/press")}
                        className="acd-btn-outline"
                        data-testid="press-form-cancel"
                    >
                        Cancelar
                    </button>
                </div>
            </form>
        </div>
    );
};

export default PressForm;
